import { useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useUser } from '@clerk/clerk-react';
import { usePrefetchStatus } from '@/lib/hooks/use-prefetch-status';
import { userService } from '@/lib/api/services/user.service';
import { freelancerService } from '@/lib/api/services/freelancer.service';
import { courseService } from '@/lib/api/services/course.service';
import { lessonProgressService } from '@/lib/api/services/lesson-progress.service';
import { lessonService } from '@/lib/api/services/lesson.service';
import type { Database } from '@/lib/supabase/supabase-types';

type Course = Database["public"]["Tables"]["courses"]["Row"];
type CourseEnrollment = Database["public"]["Tables"]["course_enrollments"]["Row"];
type Lesson = Database["public"]["Tables"]["lessons"]["Row"];
type LessonProgress = Database["public"]["Tables"]["lesson_progress"]["Row"];
type CourseSection = Database["public"]["Tables"]["course_sections"]["Row"];

type CourseWithEnrollment = Course & {
  enrollment?: CourseEnrollment | null;
};

type LessonWithProgress = Lesson & {
  progress?: LessonProgress | null;
  course: Pick<Course, 'id' | 'title' | 'slug'>;
};

type SectionWithLessons = CourseSection & {
  lessons: Lesson[];
};

type CourseDetail = Course & {
  enrollment: CourseEnrollment | null;
  sections: SectionWithLessons[];
  lessons: Lesson[];
};

export function usePrefetchUserData() {
  const { user } = useUser();
  const queryClient = useQueryClient();
  const { startPrefetch, completePrefetch, hasInitialized } = usePrefetchStatus();

  useEffect(() => {
    if (!user?.id) return;

    // Don't re-run if already initialized
    if (hasInitialized) return;

    const prefetchAllData = async () => {
      try {
        startPrefetch();
        
        // Fetch user profile
        const userProfile = await userService.getMe();

        // Cache user profile
        queryClient.setQueryData(["profile", user.id], userProfile);

        // Cache freelancer profile if user is a genius
        if (userProfile.is_genius) {
          try {
            const freelancerProfile = await freelancerService.getMyProfile();
            // Cache with both query keys for consistency
            queryClient.setQueryData(["freelancer-profile", user.id], freelancerProfile);
            queryClient.setQueryData(["freelancer-profile-by-id", freelancerProfile.id], freelancerProfile);
          } catch (error) {
            console.error('Failed to prefetch freelancer profile:', error);
          }
        }

        // Only process course data if user is a genius
        if (!userProfile.is_genius) {
          completePrefetch();
          return;
        }

        // Fetch courses, enrollments, sections, lessons, and progress in parallel
        const [courses, enrollments, allProgress] = await Promise.all([
          courseService.getCourses(),
          courseService.getMyEnrollments(),
          lessonProgressService.getMyProgress(),
        ]);

        // Create maps for O(1) lookups
        const enrollmentMap = new Map(
          enrollments.map(e => [e.course_id, e])
        );
        const progressMap = new Map(
          allProgress.map(p => [p.lesson_id, p])
        );

        // Fetch sections and lessons for all courses
        const courseDataPromises = courses.map(async (course) => {
          const [sections, lessons] = await Promise.all([
            courseService.getCourseSections(course.id),
            courseService.getCourseLessons(course.id),
          ]);
          return { course, sections, lessons };
        });

        const courseDataArray = await Promise.all(courseDataPromises);

        // Group lessons by section
        const lessonsBySection = new Map<string, Lesson[]>();
        courseDataArray.forEach(({ lessons }) => {
          lessons.forEach(lesson => {
            if (lesson.section_id) {
              const existing = lessonsBySection.get(lesson.section_id) || [];
              existing.push(lesson);
              lessonsBySection.set(lesson.section_id, existing);
            }
          });
        });

        // Cache courses with enrollment
        const coursesWithEnrollment: CourseWithEnrollment[] = courses.map(course => ({
          ...course,
          enrollment: enrollmentMap.get(course.id) || null,
        }));
        queryClient.setQueryData(["courses", user.id], coursesWithEnrollment);

        // Cache individual course details and lessons
        courseDataArray.forEach(({ course, sections, lessons }) => {
          const sectionsWithLessons = sections.map(section => ({
            ...section,
            lessons: lessonsBySection.get(section.id) || [],
          }));

          // Cache course detail
          const courseDetail: CourseDetail = {
            ...course,
            enrollment: enrollmentMap.get(course.id) || null,
            sections: sectionsWithLessons,
            lessons,
          };
          queryClient.setQueryData(["courses", "detail", course.slug, user.id], courseDetail);

          // Cache individual lessons with progress
          lessons.forEach(lesson => {
            const lessonWithProgress: LessonWithProgress = {
              ...lesson,
              progress: progressMap.get(lesson.id) || null,
              course: {
                id: course.id,
                title: course.title,
                slug: course.slug,
              },
            };
            queryClient.setQueryData(["lesson", lesson.id, user.id], lessonWithProgress);
          });

          // Cache course navigation for each lesson
          if (lessons.length > 0) {
            lessons.forEach((lesson, index) => {
              queryClient.setQueryData(["course-navigation", course.id, lesson.id], {
                currentIndex: index,
                totalLessons: lessons.length,
                previousLesson: index > 0 ? lessons[index - 1] : null,
                nextLesson: index < lessons.length - 1 ? lessons[index + 1] : null,
                allLessons: lessons.map(l => ({
                  id: l.id,
                  title: l.title,
                  position: l.position,
                  section_id: l.section_id,
                })),
              });
            });
          }
        });

      } catch (error) {
        console.error('Failed to prefetch user data:', error);
      } finally {
        // Always mark as complete, even on error
        completePrefetch();
      }
    };

    // Start prefetching after a short delay to avoid blocking initial render
    const timeoutId = setTimeout(prefetchAllData, 100);

    return () => {
      clearTimeout(timeoutId);
    };
  }, [user?.id, queryClient, startPrefetch, completePrefetch, hasInitialized]);
}

// Hook for prefetching specific course data on hover/focus
export function usePrefetchCourse() {
  const { user } = useUser();
  const queryClient = useQueryClient();

  const prefetchCourse = async (slug: string) => {
    if (!user?.id) return;

    try {
      // Check if already cached
      const existing = queryClient.getQueryData(["courses", "detail", slug, user.id]);
      if (existing) return;

      await queryClient.prefetchQuery({
        queryKey: ["courses", "detail", slug, user.id],
        queryFn: async () => {
          // Get course by slug
          const course = await courseService.getCourseBySlug(slug);

          // Get user's enrollment for this course
          const enrollments = await courseService.getMyEnrollments();
          const enrollment = enrollments.find(e => e.course_id === course.id) || null;

          // Get course sections and lessons
          const [sections, lessons] = await Promise.all([
            courseService.getCourseSections(course.id),
            courseService.getCourseLessons(course.id),
          ]);

          const sectionsWithLessons = sections.map(section => ({
            ...section,
            lessons: lessons.filter(lesson => lesson.section_id === section.id),
          }));

          return {
            ...course,
            enrollment: enrollment || null,
            sections: sectionsWithLessons,
            lessons,
          };
        },
        staleTime: 2 * 60 * 1000, // 2 minutes
      });
    } catch (error) {
      console.error(`Failed to prefetch course ${slug}:`, error);
    }
  };

  return { prefetchCourse };
}

// Hook for prefetching specific lesson data on hover/focus
export function usePrefetchLesson() {
  const { user } = useUser();
  const queryClient = useQueryClient();

  const prefetchLesson = async (lessonId: string) => {
    if (!user?.id) return;

    try {
      // Check if already cached
      const existing = queryClient.getQueryData(["lesson", lessonId, user.id]);
      if (existing) return;

      await queryClient.prefetchQuery({
        queryKey: ["lesson", lessonId, user.id],
        queryFn: async () => {
          // Get lesson with course info and progress
          const lessonWithProgress = await lessonService.getLesson(lessonId);

          // Also prefetch course navigation
          if (lessonWithProgress.course_id) {
            queryClient.prefetchQuery({
              queryKey: ["course-navigation", lessonWithProgress.course_id, lessonId],
              queryFn: async () => {
                const navigation = await lessonService.getCourseNavigation(lessonWithProgress.course_id);
                const currentIndex = navigation.lessons.findIndex(l => l.id === lessonId);
                const previousLesson = currentIndex > 0 ? navigation.lessons[currentIndex - 1] : null;
                const nextLesson = currentIndex < navigation.lessons.length - 1 ? navigation.lessons[currentIndex + 1] : null;

                return {
                  currentIndex,
                  totalLessons: navigation.totalLessons,
                  previousLesson,
                  nextLesson,
                  allLessons: navigation.lessons,
                };
              },
              staleTime: 5 * 60 * 1000,
            });
          }

          return lessonWithProgress;
        },
        staleTime: 2 * 60 * 1000, // 2 minutes
      });
    } catch (error) {
      console.error(`Failed to prefetch lesson ${lessonId}:`, error);
    }
  };

  return { prefetchLesson };
}